'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useRef, useState, type ComponentProps } from 'react';

// A link that prefetches on intent, not on sight (speed plan, 2026-09-25).
//
// Next prefetches every <Link> that scrolls into view. Our pages are
// rendered per request, so each prefetch is also a server render with its
// database reads: a page view fetched 7-24 addresses in the background -
// the footer's links, the language switch, breadcrumbs, cards, the listing
// wizard - most never visited. This one waits for a pointer over it, a
// finger on it or keyboard focus, which comes a few hundred milliseconds
// before the click (the pattern in Next's own prefetching guide).
//
// The prefetch is asked for directly: switching the prop alone is enough on
// hover (Next prefetches on mouse enter), but not on focus or touch, where
// the link is already on screen and Next's viewport check has passed.
export default function IntentLink({ prefetch, onMouseEnter, onTouchStart, onFocus, ...props }: ComponentProps<typeof Link>) {
  const router = useRouter();
  const [wanted, setWanted] = useState(false);
  const asked = useRef(false);
  const intent = () => {
    setWanted(true);
    if (asked.current || prefetch === false || typeof props.href !== 'string') return;
    asked.current = true;
    router.prefetch(props.href);
  };
  return (
    <Link
      {...props}
      prefetch={wanted ? (prefetch ?? null) : false}
      onMouseEnter={(e) => {
        intent();
        onMouseEnter?.(e);
      }}
      onTouchStart={(e) => {
        intent();
        onTouchStart?.(e);
      }}
      onFocus={(e) => {
        intent();
        onFocus?.(e);
      }}
    />
  );
}
